import { FC, useEffect } from 'react'

import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'
import { useCreateCampaignEvents } from 'src/Hooks/Analytics/useCreateCampaignEvents'
import { useCustomSearchParams } from 'src/Hooks/useCustomSearchParams'
import { createFirstCampaignStartEvent } from 'src/Helpers/TagManager'
import { ArtistRoutesEnum } from 'src/Router/ArtistComponentsNewUI'
import { PublicRoutesEnum } from 'src/Router/PublicComponentsNewUI'
import { variables } from 'src/Styled/variables'
import { APP } from 'src/Configs/App'
import IconSpotify from 'src/Assets/Svg/spotify.svg?react'
import TikTokIcon from 'src/Assets/Svg/tiktok.svg?react'
import TikTokIconLight from 'src/Assets/Svg/tiktok_light.svg?react'
import MetaAdsIcon from 'src/Assets/Svg/platform-icons/MetaAds.svg?react'
import { useThemeMode } from 'src/Hooks/useThemeMode'

import { NoAuthPageLayout } from '../../Components/NoAuthPageLayout'
import { HelmetTitle } from '../../Components/HelmetTitle'
import { PlatformButton } from '../../Components/PlatformButton'
import { WelcomeAlert } from '../../Components/WelcomeAlert'

const ContentContainer = styled.div`
  padding-top: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 80px;
`

const TopContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 960px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
`

const Title = styled.h2`
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.color.darkGrey};
  text-align: center;
  font-size: ${variables.fontSize28};
  font-style: normal;
  font-weight: ${variables.fontWeight600};
  line-height: 150%;
`

const TitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`

const Subtitle = styled.span`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: ${variables.fontSize18};
  font-style: normal;
  font-weight: ${variables.fontWeight400};
  line-height: 150%;
`

const PlatformsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`

export const ChoosePlatformNoAuthPage: FC = () => {
  const navigate = useNavigate()
  const { search } = useCustomSearchParams()
  const { campaignCreationStartMpEventHandler } = useCreateCampaignEvents()
  const { resolvedTheme } = useThemeMode()
  const TikTok = resolvedTheme === 'dark' ? TikTokIconLight : TikTokIcon
  useEffect(() => {
    createFirstCampaignStartEvent()
  }, [])

  const onSpotifyClick = (): void => {
    campaignCreationStartMpEventHandler({
      platformType: CampaignPlatformTypeEnum.SPOTIFY,
    })

    const spotifyPath = APP.PRE_RELEASE_CAMPAIGNS_ENABLED
      ? PublicRoutesEnum.CreateSpotifyCampaignReleaseType
      : PublicRoutesEnum.CreateSpotifyCampaign

    navigate(`${spotifyPath}?${search.toString()}`)
  }
  const onTikTokClick = (): void => {
    campaignCreationStartMpEventHandler({
      platformType: CampaignPlatformTypeEnum.TIKTOK,
    })
    navigate(`${ArtistRoutesEnum.CreateTikTok}?${search.toString()}`)
  }

  const onAdsClick = (): void => {
    campaignCreationStartMpEventHandler({
      platformType: CampaignPlatformTypeEnum.META,
    })
    navigate(`${ArtistRoutesEnum.CreateAds}?${search.toString()}`)
  }

  return (
    <NoAuthPageLayout>
      <HelmetTitle />
      <WelcomeAlert />
      <ContentContainer>
        <TopContentContainer>
          <TitleContainer>
            <Title>Create new campaign</Title>
            <Subtitle>Choose type of campaign to start</Subtitle>
          </TitleContainer>
          <PlatformsContainer>
            <PlatformButton
              icon={IconSpotify}
              title='Spotify Campaign'
              onClick={onSpotifyClick}
            />
            <PlatformButton
              icon={TikTok}
              title='TikTok Campaign'
              onClick={onTikTokClick}
            />
            {APP.ENABLE_META_ADS && (
              <PlatformButton
                icon={MetaAdsIcon}
                title={'Meta Ads Campaign'}
                onClick={onAdsClick}
              />
            )}
          </PlatformsContainer>
        </TopContentContainer>
      </ContentContainer>
    </NoAuthPageLayout>
  )
}
